import { useEffect, useState } from 'react';
import type { PreCannedReportFilter } from '@/services/preCannedReportsService';

export type ArFilterValues = Record<string, string>;

type Props = {
  filters: PreCannedReportFilter[];
  parameters: Record<string, string>;
  disabled: boolean;
  onApply: (values: ArFilterValues) => void;
};

function valuesFromParameters(
  filters: PreCannedReportFilter[],
  parameters: Record<string, string>,
): ArFilterValues {
  return Object.fromEntries(
    filters.map((filter) => [
      filter.id,
      String(parameters[filter.id] ?? filter.default ?? ''),
    ]),
  );
}

function defaultValues(
  filters: PreCannedReportFilter[],
): ArFilterValues {
  return Object.fromEntries(
    filters.map((filter) => [
      filter.id,
      String(filter.default ?? ''),
    ]),
  );
}

export default function ArFilterBar({
  filters = [] ,
  parameters,
  disabled,
  onApply,
}: Props) {
  const [draft, setDraft] = useState<ArFilterValues>(() =>
    valuesFromParameters(filters, parameters),
  );

  // Synchronize only when definitions or applied filter values change.
  // Other response metadata updates must not erase an unfinished edit.
  const appliedSignature = JSON.stringify(
    valuesFromParameters(filters, parameters),
  );

  useEffect(() => {
    setDraft(JSON.parse(appliedSignature) as ArFilterValues);
  }, [appliedSignature]);

  const controlClass =
    'h-9 rounded-full border border-[#EDE6E1] bg-white px-3 ' +
    'text-sm text-[#1F1A20] disabled:cursor-not-allowed disabled:opacity-50';

  const update = (id: string, value: string) => {
    setDraft((previous) => ({
      ...previous,
      [id]: value,
    }));
  };

  return (
    <form
      className="flex flex-wrap items-end gap-3"
      onSubmit={(event) => {
        event.preventDefault();

        const values = Object.fromEntries(
          filters.map((filter) => [
            filter.id,
            filter.disabled
              ? String(filter.default ?? '')
              : String(draft[filter.id] ?? '').trim(),
          ]),
        );

        onApply(values);
      }}
    >
      {filters.map((filter) => (
        <label
          key={filter.id}
          className="grid gap-1 text-xs font-semibold"
          title={filter.help_text || undefined}
        >
          <span>{filter.label}</span>

          {filter.type === 'select' ? (
            <select
              className={controlClass}
              value={draft[filter.id] ?? ''}
              disabled={disabled || Boolean(filter.disabled)}
              onChange={(event) =>
                update(filter.id, event.target.value)
              }
            >
              {(filter.options || []).map((option, index) => {
                const value = String(option.value ?? '');

                return (
                  <option key={`${value}-${index}`} value={value}>
                    {option.label ?? value}
                  </option>
                );
              })}
            </select>
          ) : (
            <input
              type={filter.type === 'date' ? 'date' : 'text'}
              className={controlClass}
              value={draft[filter.id] ?? ''}
              placeholder={filter.placeholder || undefined}
              disabled={disabled || Boolean(filter.disabled)}
              onChange={(event) =>
                update(filter.id, event.target.value)
              }
            />
          )}
        </label>
      ))}

      <button
        type="submit"
        disabled={disabled}
        className="h-9 rounded-full bg-[#7A003C] px-5 text-sm font-semibold text-white disabled:opacity-50"
      >
        Apply
      </button>

      <button
        type="button"
        disabled={disabled}
        className={controlClass}
        onClick={() => {
          const values = defaultValues(filters);
          setDraft(values);
          onApply(values);
        }}
      >
        Reset
      </button>
    </form>
  );
}